import { IconButton, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { formatTransportPrice } from '../formatters/transportPriceFormatter';
import { TransportDistance } from '../../../../../shared/types/transport';

const extractNumber = (value: string) => {
  const match = value.match(/\d+/);
  return match ? parseInt(match[0], 10) : 0;
};

const commonColumnProps = {
  editable: true,
  sortable: false,
  lockPosition: true,
  flex: 1,
  cellStyle: { textAlign: 'center' },
  headerClass: 'center-header',
};

/**
 * Generates column definitions for AgGrid based on transport distances.
 * @param transportDistances - Array of TransportDistance objects.
 * @returns Column definitions for AgGrid.
 */
export const TransportColumns = (
  transportDistances: TransportDistance[],
  handleDeleteRow: (id: number) => void,
): ColDef[] => {
  if (!transportDistances) return [];

  const distanceColumns: ColDef[] = transportDistances
    .sort((a, b) => a.distanceMin - b.distanceMin)
    .map((distance) => {
      const distanceRange = `${distance.distanceMin}-${distance.distanceMax}`;

      return {
        ...commonColumnProps,
        headerName: `${distanceRange} km`,
        field: distanceRange,
        minWidth: 140,
        valueFormatter: (params) =>
          formatTransportPrice(params.value),
      };
    });

  return [
    {
      headerName: 'Toiminnot',
      field: 'actions',
      sortable: false,
      width: 80,
      cellRenderer: (params: ICellRendererParams) => (
        <Tooltip title="Poista rivi">
          <IconButton
            onClick={() => handleDeleteRow(params.data.id)}
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      ),
      cellStyle: {
        textAlign: 'center',
        borderRight: '1px solid #ccc',
      },
      headerClass: 'center-header',
    },
    {
      ...commonColumnProps,
      headerName: 'Painoraja',
      field: 'upToWeight',
      pinned: 'left',
      minWidth: 100,
      valueFormatter: (params) =>
        params.value ? `${params.value} kg` : '',
    },
    {
      ...commonColumnProps,
      headerName: 'Rahtiluokka',
      field: 'class',
      pinned: 'left',
      minWidth: 150,
      sort: 'asc',
      comparator: (valueA, valueB) =>
        extractNumber(valueA) - extractNumber(valueB),
    },
    ...distanceColumns,
    {
      ...commonColumnProps,
      headerName: 'Pikatoimituslisä',
      field: 'expressDeliveryFee',
      minWidth: 140,
      valueFormatter: (params) => formatTransportPrice(params.value),
    },
    {
      ...commonColumnProps,
      headerName: 'ADR-lisä',
      field: 'adrFee',
      minWidth: 140,
      valueFormatter: (params) => formatTransportPrice(params.value),
    },
  ];
};
